import { useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { PageHeader } from '../../components/PageHeader'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import { Capacitor } from '@capacitor/core'
import { loadRulesPdf } from './rulesPdfCache'
import { useT } from '../../i18n'

// Same bundled-worker wiring as RulebookScreen — no CDN at runtime.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()

// Font rendering for the Japanese rulebooks. Their CJK text is embedded
// as CID-keyed CFF (KozGoPr6N) with Identity-H encoding — WebKit fails to
// inject those as @font-face web fonts, so the glyphs came back blank
// ("white on white"). disableFontFace makes pdf.js rasterize the glyph
// OUTLINES straight to the canvas instead, which always works when the
// font program is embedded (it is). cMap + standard-font data (copied
// into the build by the pdfjs-cmap-assets plugin in vite.config.ts) cover
// the rest of the catalog's PDFs that lean on named CMaps or non-embedded
// standard fonts. Module-level constant so react-pdf sees a stable ref.
const PDF_OPTIONS = {
  cMapUrl: '/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: '/standard_fonts/',
  disableFontFace: true,
}

const ZOOM_LEVELS = [0.75, 1, 1.25, 1.5, 2, 2.5, 3]

interface Props {
  title: string
  url: string
  onBack: () => void
  /** Fallback when the download fails — open externally instead. */
  onOpenExternal: (url: string) => void
  /** Scrolling background ribbon (game title + accent), matching the
   *  living-rulebook reader so the two documents feel like siblings. */
  bgText?: string
  bgColor?: string
}

/**
 * In-app viewer for the Rules tab's catalog PDFs. Downloads once via the
 * native layer (no CORS, no browser detour), caches on device, and
 * renders inline with PDF.js — same canvas/zoom treatment as the
 * per-game RulebookScreen.
 */
export function RulesPdfViewer({ title, url, onBack, onOpenExternal, bgText, bgColor }: Props) {
  const t = useT()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = useState<number>(0)
  const [numPages, setNumPages] = useState<number>(0)
  const [zoomIndex, setZoomIndex] = useState<number>(1)
  const [bytes, setBytes] = useState<Uint8Array | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ loaded: number; total: number | null } | null>(null)
  // Per-page paint tracking. Image-heavy pages take SECONDS to paint
  // and pdf.js fills the canvas progressively, so a half-painted page
  // is indistinguishable from a finished one — Jordan hit exactly that
  // on the Turin Market JP book (2 tall pages, 179 JPEGs). Pages
  // report in via onRenderSuccess; until then a chip says so, and the
  // header line reads "Rendering page X of Y". Keyed by render pass so
  // zoom changes re-arm the tracking.
  const [renderedPages, setRenderedPages] = useState<ReadonlySet<number>>(new Set())
  const [failedPages, setFailedPages] = useState<ReadonlySet<number>>(new Set())
  const [retryNonce, setRetryNonce] = useState(0)
  // height/width per page, read from the document as soon as it opens.
  // Every page reserves its true height BEFORE it paints, so a slow
  // page can't collapse to nothing and let the next one take its place
  // on screen (which is how the Turin JP book opened on page 2).
  const [pageAspects, setPageAspects] = useState<number[]>([])

  useEffect(() => {
    let cancelled = false
    setBytes(null); setError(null); setNumPages(0); setProgress(null)
    loadRulesPdf(url, (loaded, total) => {
      if (!cancelled) setProgress({ loaded, total })
    })
      .then(b => { if (!cancelled) setBytes(b) })
      .catch(e => { if (!cancelled) setError(String(e?.message ?? e)) })
    return () => { cancelled = true }
  }, [url])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    // Quantize the measured width. Every change to pageWidth CANCELS and
    // restarts the render of every page, and a heavy page (the Turin
    // Market JP book is 15,400 ops over 112 images) takes long enough
    // that it never survives one: as its pages paint, the document grows
    // tall enough to summon a scrollbar, the scrollbar narrows this
    // container, the narrower width restarts the render, and the reader
    // is left staring at whatever the last aborted pass had painted.
    // Rounding to 16px steps absorbs scrollbar and sub-pixel jitter, and
    // scrollbar-gutter on the scroller below keeps the gutter reserved so
    // the width usually never moves at all.
    const measure = () => {
      const q = Math.max(0, Math.floor(el.clientWidth / 16) * 16)
      setContainerWidth(prev => (prev === q ? prev : q))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // react-pdf mutates/transfers the buffer it's handed — give it a copy
  // and keep ours stable across re-renders.
  const file = useMemo(() => (bytes ? { data: bytes.slice() } : null), [bytes])

  const scale = ZOOM_LEVELS[zoomIndex]
  const pageWidth = containerWidth > 0 ? containerWidth * scale : undefined

  // A zoom change (or retry) redraws every canvas — re-arm the
  // per-page tracking so the chips come back until pages re-report.
  useEffect(() => {
    setRenderedPages(new Set())
    setFailedPages(new Set())
  }, [file, pageWidth, retryNonce])

  const allRendered = numPages > 0 && renderedPages.size >= numPages && failedPages.size === 0

  return (
    <div className="screen">
      {bgText && <BackgroundTitle text={bgText} color={bgColor} />}
      <PageHeader title={title} subtitle={t('Rules PDF')} onBack={onBack} />

      {/* scrollbar-gutter keeps space reserved for the scrollbar, so the
          PDF container's width doesn't change the moment the rendered
          pages make this scroller overflow (see measure() above). */}
      <div className="px-5 py-3 space-y-3 flex-1 overflow-y-auto pb-24
                      scrollbar-gutter-stable">
        <div className="flex items-center justify-between gap-2">
          {/* Paint status — visible until every page has ACTUALLY
              finished drawing, so a slow half-painted page can't pass
              for the whole document. */}
          {numPages > 0 ? (
            failedPages.size > 0 ? (
              <button
                type="button"
                onClick={() => setRetryNonce(n => n + 1)}
                className="text-xs font-semibold text-brand-danger active:scale-95 transition-transform"
              >
                {failedPages.size === 1
                  ? t("1 page didn't finish. Tap to retry.")
                  : t("{n} pages didn't finish. Tap to retry.", { n: failedPages.size })}
              </button>
            ) : allRendered ? (
              <span className="text-xs text-brand-muted">
                {numPages === 1
                  ? t('1 page · fully loaded')
                  : t('{n} pages · fully loaded', { n: numPages })}
              </span>
            ) : (
              <span className="text-xs text-brand-muted animate-pulse">
                {t('Rendering page {n} of {total}…', {
                  n: Math.min(renderedPages.size + 1, numPages), total: numPages,
                })}
              </span>
            )
          ) : <span />}
          <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setZoomIndex(i => Math.max(0, i - 1))}
            disabled={zoomIndex === 0}
            aria-label={t('Zoom out')}
            className="w-8 h-8 rounded-full bg-brand-elevated text-brand-text
                       text-base font-bold active:scale-90 transition-transform
                       disabled:opacity-30"
          >−</button>
          <span className="text-xs font-medium text-brand-muted w-10 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            onClick={() => setZoomIndex(i => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
            disabled={zoomIndex === ZOOM_LEVELS.length - 1}
            aria-label={t('Zoom in')}
            className="w-8 h-8 rounded-full bg-brand-elevated text-brand-text
                       text-base font-bold active:scale-90 transition-transform
                       disabled:opacity-30"
          >+</button>
          </div>
        </div>

        <div
          ref={containerRef}
          className="bg-white border-2 border-brand-border rounded-2xl shadow-card overflow-x-auto"
        >
          {error ? (
            <div className="p-8 text-center">
              <p className="font-display text-lg font-bold text-brand-text">
                {t("Couldn't load this rulebook")}
              </p>
              <p className="text-sm text-brand-muted mt-2">{error}</p>
              <button
                type="button"
                onClick={() => onOpenExternal(url)}
                className="inline-block mt-4 px-4 py-2 rounded-full bg-brand-elevated
                           text-sm font-semibold text-brand-text active:scale-95 transition-transform"
              >
                {t('Open in browser instead')}
              </button>
            </div>
          ) : !file ? (
            <div className="p-8 text-center text-brand-muted text-sm">
              Downloading rulebook…
              {/* Real byte progress; when the server won't say how big
                  the file is, the bar pulses instead of lying. */}
              <div className="max-w-xs mx-auto mt-3 h-1.5 rounded-full bg-brand-elevated overflow-hidden">
                <div
                  className={`h-full rounded-full bg-brand-lime-dark transition-all duration-200
                              ${progress?.total ? '' : 'animate-pulse'}`}
                  style={{
                    width: progress?.total
                      ? `${Math.min(100, Math.round((progress.loaded / progress.total) * 100))}%`
                      : progress ? '100%' : '4%',
                  }}
                />
              </div>
              {progress?.total ? (
                <span className="block text-xs mt-2">
                  {t('{loaded} of {total} MB', {
                    loaded: (progress.loaded / 1_048_576).toFixed(1),
                    total: (progress.total / 1_048_576).toFixed(1),
                  })}
                </span>
              ) : null}
              {Capacitor.isNativePlatform() && (
                <span className="block text-xs mt-1">
                  {t("First open only. It's saved on your phone after this.")}
                </span>
              )}
            </div>
          ) : (
            <Document
              file={file}
              options={PDF_OPTIONS}
              onLoadSuccess={(pdf) => {
                setNumPages(pdf.numPages); setError(null)
                void (async () => {
                  const a: number[] = []
                  for (let i = 1; i <= pdf.numPages; i++) {
                    const v = (await pdf.getPage(i)).getViewport({ scale: 1 })
                    a.push(v.height / v.width)
                  }
                  setPageAspects(a)
                })()
              }}
              onLoadError={(err) => setError(err.message)}
              loading={
                <div className="p-8 text-center text-brand-muted text-sm">
                  {t('Rendering…')}
                </div>
              }
              className="flex flex-col items-center"
            >
              {Array.from({ length: numPages }, (_, i) => {
                const n = i + 1
                const done = renderedPages.has(n)
                const failed = failedPages.has(n)
                // Paint STRICTLY in order: a page starts only once the
                // one before it has settled. Rendering every page at
                // once made the light pages finish first and starved
                // the heavy ones (Turin JP page 1 carries 177 image
                // paints against page 2's 79), which is why page 1 was
                // the one left blank.
                const ready = n === 1 || renderedPages.has(n - 1) || failedPages.has(n - 1)
                const aspect = pageAspects[i]
                const reserved = !done && aspect && pageWidth
                  ? Math.round(pageWidth * aspect)
                  : undefined
                return (
                  <div
                    key={`page-${n}-${retryNonce}`}
                    style={reserved ? { minHeight: reserved } : undefined}
                    className="relative w-full flex justify-center [overflow-anchor:none]"
                  >
                    {ready && (
                      <Page
                        pageNumber={n}
                        width={pageWidth}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                        className="border-b border-brand-border last:border-b-0"
                        onRenderSuccess={() => setRenderedPages(prev => new Set(prev).add(n))}
                        onRenderError={() => setFailedPages(prev => new Set(prev).add(n))}
                      />
                    )}
                    {/* Corner chip until this page has actually finished
                        painting — the canvas fills in progressively, so
                        without it a half-drawn page looks complete. */}
                    {!done && (
                      <span className={`absolute top-2 right-2 text-[10px] font-semibold
                                        rounded-full px-2 py-0.5 shadow-xs border
                                        ${failed
                                          ? 'bg-brand-danger text-white border-brand-danger'
                                          : 'bg-white/90 text-brand-muted border-brand-border animate-pulse'}`}>
                        {failed
                          ? t("Didn't finish")
                          : ready
                            ? t('Rendering page {n}…', { n })
                            : t('Page {n} queued', { n })}
                      </span>
                    )}
                  </div>
                )
              })}
            </Document>
          )}
        </div>
      </div>
    </div>
  )
}
